import { Check, Sparkles, Crown, Building2, ArrowLeft, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import BrandLogo from "./BrandLogo";
import PaymentsComingSoon from "./PaymentsComingSoon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    id: "free",
    name: "Gratuito",
    price: "0€",
    period: "para siempre",
    icon: Sparkles,
    highlight: false,
    features: [
      "3 análisis gratuitos de por vida",
      "Informe completo de 30+ módulos",
      "Exportación a PDF",
      "Enlaces compartibles",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: "7,99 $",
    period: "/mes",
    icon: Crown,
    highlight: true,
    features: [
      "Análisis ilimitados",
      "Comparación de videos lado a lado",
      "Análisis de comentarios con IA",
      "Historial completo de análisis",
      "Exportación a PDF y enlaces compartibles",
      "Soporte prioritario",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "Contacto",
    period: "",
    icon: Building2,
    highlight: false,
    features: [
      "Todo lo del Plan Pro",
      "Múltiples usuarios por cuenta",
      "Análisis de canales completos",
      "API de acceso",
    ],
  },
];

export default function PricingPage() {
  const { user, login } = useAuth();
  const [paying, setPaying] = useState(false);
  const [payingPack, setPayingPack] = useState(null);
  const [payError, setPayError] = useState(null);
  const [payUnavailable, setPayUnavailable] = useState(false);
  const [paymentsEnabled, setPaymentsEnabled] = useState(null);
  const [comingSoon, setComingSoon] = useState(null);
  const [whop, setWhop] = useState({ configured: false, checkout_url: null });

  useEffect(() => {
    axios.get(`${API}/payments/config`)
      .then((r) => setPaymentsEnabled(!!r.data.enabled))
      .catch(() => setPaymentsEnabled(true));
    axios.get(`${API}/whop/config`)
      .then((r) => setWhop({ configured: !!r.data.configured, checkout_url: r.data.checkout_url || null }))
      .catch(() => setWhop({ configured: false, checkout_url: null }));
  }, []);

  const whopReady = whop.configured && !!whop.checkout_url;

  const logIntent = (packageId) => {
    if (!user) return;
    axios.post(`${API}/purchase-intent`, {
      package_id: packageId,
      origin: localStorage.getItem("vt_origin") || "directo",
    }).catch(() => {});
  };

  const startCheckout = async (packageId) => {
    if (!user) {
      login();
      return;
    }
    logIntent(packageId);
    if (paymentsEnabled === false) {
      setComingSoon({
        packageId,
        label: packageId === "pro_monthly" ? "Plan Pro" : packageId.replace("credits_", "") + " Análisis Extra",
      });
      return;
    }
    if (packageId === "pro_monthly") setPaying(true);
    else setPayingPack(packageId);
    setPayError(null);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      if (!r.data?.checkout_url) {
        throw new Error("Sin URL de pago");
      }
      window.location.href = r.data.checkout_url;
    } catch (err) {
      if (err.response?.status === 502 || !err.response) {
        setPayUnavailable(true);
        setPayError("Los pagos no están disponibles en este momento. Inténtalo más tarde.");
      } else {
        setPayError("No se pudo iniciar el pago. Intenta de nuevo.");
      }
      setPaying(false);
      setPayingPack(null);
    }
  };

  const handleUpgrade = () => {
    if (!user) {
      login();
      return;
    }
    logIntent("pro_monthly");
    if (!whopReady) {
      setComingSoon({ packageId: "pro_monthly", label: "Plan Pro" });
      return;
    }
    setPaying(true);
    window.location.href = whop.checkout_url;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-purple-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center">
            <BrandLogo size="sm" />
          </a>
          <a href="/" data-testid="pricing-back-link" className="flex items-center gap-2 text-sm font-semibold text-purple-700 hover:text-purple-900">
            <ArrowLeft className="w-4 h-4" /> Volver a la app
          </a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-14">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-4">
            Planes y <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Precios</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Empieza gratis con 3 análisis. Pasa a Pro cuando necesites analizar sin límites.
          </p>
        </div>

        <div data-testid="pricing-plans" className="grid md:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((plan) => {
            const Icon = plan.icon;
            const isCurrent = user && user.plan === plan.id;
            return (
              <div
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`relative rounded-3xl p-8 flex flex-col ${plan.highlight ? "bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-2xl scale-[1.03]" : "bg-white text-gray-900 shadow-lg border border-purple-100"}`}
              >
                {plan.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-brand-red to-pink-600 text-white text-xs font-bold px-4 py-1 shadow-[0_0_18px_rgba(224,0,0,0.45)] rounded-full shadow">
                    RECOMENDADO
                  </span>
                )}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${plan.highlight ? "bg-white/20" : "bg-purple-100"}`}>
                  <Icon className={`w-6 h-6 ${plan.highlight ? "text-white" : "text-purple-600"}`} />
                </div>
                <h2 className="text-2xl font-bold mb-1">{plan.name}</h2>
                <div className="mb-6">
                  <span className="text-3xl font-extrabold">{plan.price}</span>
                  {plan.period && <span className={`text-sm ml-2 ${plan.highlight ? "text-purple-100" : "text-gray-500"}`}>{plan.period}</span>}
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.highlight ? "text-pink-200" : "text-purple-400"}`} />
                      {f}
                    </li>
                  ))}
                </ul>

                {isCurrent ? (
                  <div data-testid={`current-plan-${plan.id}`} className={`text-center py-3 rounded-xl font-semibold text-sm ${plan.highlight ? "bg-white/20" : "bg-purple-50 text-purple-700 border border-purple-200"}`}>
                    ✓ Tu plan actual
                  </div>
                ) : plan.id === "free" ? (
                  <button
                    data-testid="pricing-free-cta"
                    onClick={() => (user ? (window.location.href = "/") : login())}
                    className="w-full py-3 rounded-xl font-semibold text-sm bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-all"
                  >
                    {user ? "Ir a analizar" : "Empezar gratis con Google"}
                  </button>
                ) : plan.id === "pro" ? (
                  <button
                    data-testid="pricing-pro-cta"
                    onClick={handleUpgrade}
                    disabled={paying}
                    className="w-full py-3 rounded-xl font-semibold text-sm bg-white text-purple-700 hover:bg-purple-50 shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {paying ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Redirigiendo a Whop...
                      </>
                    ) : user ? (
                      "Actualizar a Pro — 7,99 $/mes"
                    ) : (
                      "Inicia sesión y hazte Pro"
                    )}
                  </button>
                ) : (
                  <a
                    href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL}?subject=Plan%20Enterprise%20ViralTubeAI`}
                    data-testid="pricing-enterprise-cta"
                    className="block text-center w-full py-3 rounded-xl font-semibold text-sm bg-gray-900 text-white hover:bg-gray-800 transition-all"
                  >
                    Contactar ventas
                  </a>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-center text-sm text-gray-500 mt-10">
          {payError && <span data-testid="pay-error" className="block text-red-600 font-semibold mb-2">{payError}</span>}
          Pagos procesados de forma segura con Whop. Puedes cancelar cuando quieras.
        </p>

        {/* Paquetes de créditos */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">
              ¿No quieres suscripción? <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Compra créditos</span>
            </h2>
            <p className="text-gray-600">Análisis adicionales de pago único. No caducan nunca.</p>
          </div>
          <div data-testid="credit-packs" className="grid sm:grid-cols-3 gap-5 max-w-3xl mx-auto">
            {[
              { id: "credits_10", credits: 10, price: "2,99 $", per: "0,30 $/análisis", popular: false },
              { id: "credits_50", credits: 50, price: "9,99 $", per: "0,20 $/análisis", popular: true },
              { id: "credits_100", credits: 100, price: "14,99 $", per: "0,15 $/análisis", popular: false },
            ].map((pack) => (
              <div
                key={pack.id}
                data-testid={`credit-pack-${pack.id}`}
                className={`relative bg-white rounded-2xl p-6 text-center shadow-lg border ${pack.popular ? "border-purple-400 ring-2 ring-purple-200" : "border-purple-100"}`}
              >
                {pack.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">MÁS POPULAR</span>
                )}
                <div className="text-4xl font-extrabold text-gray-900 mb-1">{pack.credits}</div>
                <div className="text-sm text-gray-500 mb-3">análisis extra</div>
                <div className="text-2xl font-bold text-purple-700 mb-1">{pack.price}</div>
                <div className="text-xs text-gray-400 mb-5">{pack.per}</div>
                <button
                  data-testid={`buy-${pack.id}`}
                  onClick={() => startCheckout(pack.id)}
                  disabled={payingPack === pack.id || payUnavailable}
                  className="w-full py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {payingPack === pack.id ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Redirigiendo...</>
                  ) : user ? "Comprar" : "Inicia sesión y compra"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </main>

      {comingSoon && (
        <PaymentsComingSoon
          packageId={comingSoon.packageId}
          planLabel={comingSoon.label}
          onClose={() => setComingSoon(null)}
        />
      )}
    </div>
  );
}
